import React, {useContext, useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {addDays, format, getISODay, isValid, parseISO, startOfISOWeek} from 'date-fns';
import {Alert, Box, FormControl, InputLabel, LinearProgress, MenuItem, Select, Typography} from '@mui/material';
import {store} from '../store';
import {hasScope} from '../helpers/scopes';
import SelectWeek from '../Rota/SelectWeek/SelectWeek';
import {getPerformance} from './api';
import HoursReport from './HoursReport';
import DateRangeNew from '../molcules/DateRange/DateRangeNew';

const validDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') && isValid(parseISO(value));

export default function Performance({siteOnly = false}) {
  const {state} = useContext(store);
  const siteId = siteOnly ? state.site?._id : undefined;
  const permitted = hasScope(state.user, 'rota:admin-read');
  const canSeeCosts = hasScope(state.user, 'rota:cost-read');
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const custom = params.get('mode') === 'custom';
  const selected = params.get('startDate');
  const parsed = parseISO(selected || '');
  const weekStart = /^\d{4}-\d{2}-\d{2}$/.test(selected || '') && isValid(parsed) && getISODay(parsed) === 1
    ? parsed : startOfISOWeek(new Date());
  const dates = {weekStart, weekEnd: addDays(weekStart, 6)};
  const startDate = format(weekStart, 'yyyy-MM-dd');
  const validRange = validDate(params.get('from')) && validDate(params.get('to')) && params.get('from') <= params.get('to');
  const from = custom && validRange ? params.get('from') : startDate;
  const to = custom && validRange ? params.get('to') : format(dates.weekEnd, 'yyyy-MM-dd');
  const periodKey = custom ? `${from}:${to}` : startDate;
  useEffect(() => {
    if (custom) {
      if (!validRange || selected != null) {
        const params = new URLSearchParams(location.search);
        params.delete('startDate');
        params.set('from', from);
        params.set('to', to);
        history.replace({...location, search: `?${params}`});
      }
    } else if (selected !== startDate) {
      const params = new URLSearchParams(location.search);
      params.set('startDate', startDate);
      history.replace({...location, search: `?${params}`});
    }
  }, [history, location, selected, startDate, custom, validRange, from, to]);
  const setDates = ({weekStart: nextStart}) => {
    const params = new URLSearchParams(location.search);
    params.set('startDate', format(nextStart, 'yyyy-MM-dd'));
    history.push({...location, search: `?${params}`});
  };
  const setCustomDates = range => {
    if (!range || range.length !== 2 || !range.every(isValid) || range[0] > range[1]) return;
    const params = new URLSearchParams(location.search);
    params.set('mode', 'custom');
    params.delete('startDate');
    params.set('from', format(range[0], 'yyyy-MM-dd'));
    params.set('to', format(range[1], 'yyyy-MM-dd'));
    history.push({...location, search: `?${params}`});
  };
  const changeMode = mode => {
    if (mode === 'custom') return setCustomDates([weekStart, dates.weekEnd]);
    const params = new URLSearchParams(location.search);
    params.delete('mode');
    params.delete('from');
    params.delete('to');
    params.set('startDate', format(startOfISOWeek(parseISO(from)), 'yyyy-MM-dd'));
    history.push({...location, search: `?${params}`});
  };
  const {data, isLoading, isFetching, error} = useQuery({
    queryKey: ['performance', state.user?._id, custom ? 'custom' : 'week', periodKey, siteOnly ? siteId || 'pending-site' : 'all'],
    queryFn: () => {
      const period = custom ? {from, to} : startDate;
      return siteOnly ? getPerformance(period, siteId) : getPerformance(period);
    },
    enabled: permitted && (!siteOnly || Boolean(siteId))
  });
  if (!permitted) return <Alert severity="warning">You do not have permission to view Performance.</Alert>;
  if (siteOnly && !siteId) return <Alert severity="info">Select a site to view its analysis.</Alert>;
  const report = data?.data;
  const costEstimate = site => site.costedShifts
    ? site.costs.reduce((total, cost) => total + cost.amount, 0) : null;
  const rows = (report?.sites || []).filter(site => !siteOnly || String(site.siteId) === String(siteId)).sort((a, b) => {
    if (!canSeeCosts) return a.name.localeCompare(b.name);
    const aCost = costEstimate(a);
    const bCost = costEstimate(b);
    if (aCost == null && bCost != null) return 1;
    if (bCost == null && aCost != null) return -1;
    return (bCost - aCost) || a.name.localeCompare(b.name);
  });
  return <Box sx={{py: siteOnly ? 0: 3}}>
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3}}>
      {!siteOnly && <Typography variant="h5" component="h1">Department Rota Analysis</Typography>}
      <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2}}>
        <FormControl size="small" sx={{minWidth: 110}}>
          <InputLabel id="performance-period-label">Period</InputLabel>
          <Select labelId="performance-period-label" label="Period" value={custom ? 'custom' : 'week'}
            onChange={event => changeMode(event.target.value)}>
            <MenuItem value="week">Week</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>
        {custom ? <DateRangeNew value={[parseISO(from), parseISO(to)]} onChange={setCustomDates}
          style={{marginBottom: 0, marginRight: 0}} /> : <SelectWeek dateRange={dates} onChange={setDates} showWeekPicker />}
      </Box>
    </Box>
    {isFetching && <LinearProgress aria-label="Loading performance" />}
    {error && <Alert severity="error" sx={{my: 2}}>
      {error.errorArray?.join('. ') || error.message || 'Unable to load performance. Please try again.'}
    </Alert>}
    {report && <>
      {rows.map(site => <Box key={`${periodKey}:${site.siteId}`} sx={{mb: 4}}>
        {!siteOnly && <Typography component="h2" variant="h5" sx={{mb: 2}}>{site.name}</Typography>}
        <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', lg: site.departments?.length === 1 ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
          {(site.departments || []).map(department => <HoursReport key={department.departmentId ?? 'unassigned'}
            row={department} siteName={site.name} title={department.name} provisional={report.provisional} period={report.period} canSeeCosts={canSeeCosts} />)}
          {site.departments?.length !== 1 && <Box sx={{gridColumn: '1 / -1'}}><HoursReport row={site} siteName={site.name} title="All"
            provisional={report.provisional} period={report.period} canSeeCosts={canSeeCosts} all /></Box>}
        </Box>
      </Box>)}
      {/*<Alert severity="info" sx={{my: 2}}>
        <Typography variant="body2" sx={{mb: 1}}>
          Scheduled hours use saved shift times, which can change for future shifts. All hours show elapsed time without deducting breaks. Recorded progress includes completed approved or punched records; its dashed marker shows the schedule for those same records. Each solid marker shows the full weekly schedule.
          {' '}Totals use approved times, then complete punches. Missing punches use the corresponding scheduled times,
          including a scheduled end when only clock-in is available, for both current and past weeks.
          These estimates are included in projected totals. Only invalid or unusable times prevent a full comparison.
        </Typography>
        {canSeeCosts && <Typography variant="body2" sx={{mb: 1}}>
          Cost estimates may include break and pay adjustments. Missing estimates are not counted as zero,
          and department supplements that are not linked to a shift are excluded.
        </Typography>}
      </Alert>*/}
      {!rows.length && <Alert severity="info" sx={{mt: 2}}>No accessible sites found.</Alert>}
    </>}
    {!isLoading && !error && !report && <Alert severity="info">No performance data available.</Alert>}
  </Box>;
}
